import React, { useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AppsIcon from '@mui/icons-material/Apps';
import {
  ResultsSection,
  ChartContainer,
  SiteSection,
  SiteHeader,
  SummaryBox,
  SummaryItem,
  StyledDivider,
  SectionTitle,
  BreakdownTitle,
  SmallChartContainer,
  ExcursionEventsTitle,
  SuccessIcon
} from '#component/reports/styles';
import ExcursionChart from './excursion-chart';
import ExcursionList from './excursion-list';
import { calculateDailyTotals } from './helper';
import {
  getAllExcursions,
  countAffectedItems,
  getLabels,
  normalizeForIteration
} from './data-adapter';

const NoExcursionsState = ({ title = 'All clear', description }) => (
  <Box py={4} textAlign="center">
    <SuccessIcon color="success" />
    <Typography variant="subtitle1" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="textSecondary">
      {description}
    </Typography>
  </Box>
);

const ItemSection = ({ group, item, excursions, year, month }) => {
  const dailyTotals = useMemo(
    () => calculateDailyTotals(excursions, year, month),
    [excursions, year, month]
  );

  return (
    <SiteSection elevation={0} variant="outlined">
      <SiteHeader>
        <LocationOnIcon color="primary" fontSize="small" />
        <Chip
          label={group.label}
          size="small"
          color="primary"
          variant="outlined"
        />
        <AppsIcon color="secondary" fontSize="small" />
        <Chip
          label={item.label}
          size="small"
          color="secondary"
          variant="outlined"
        />
        {excursions.length > 0 && (
          <Chip
            label={`${excursions.length} excursions`}
            size="small"
            color="error"
          />
        )}
      </SiteHeader>

      {excursions.length > 0 ? (
        <>
          <SmallChartContainer>
            <ExcursionChart data={dailyTotals} />
          </SmallChartContainer>

          <ExcursionEventsTitle variant="subtitle2">
            Excursion Events
          </ExcursionEventsTitle>
          <ExcursionList excursions={excursions} />
        </>
      ) : (
        <NoExcursionsState
          description="No excursions were detected during this period. All monitored values remained within the defined limits."
        />
      )}
    </SiteSection>
  );
};

const ReportResults = ({ data, projectTitle, monthLabel }) => {
  const { year, month } = data;

  const allExcursions = useMemo(() => getAllExcursions(data), [data]);
  const totalExcursions = allExcursions.length;
  const affectedItems = useMemo(() => countAffectedItems(data), [data]);
  const labels = useMemo(() => getLabels(data), [data]);
  const normalizedItems = useMemo(() => {
    const items = normalizeForIteration(data);
    return [...items].sort(
      (a, b) => (b.excursions?.length > 0) - (a.excursions?.length > 0)
    );
  }, [data]);

  const dailyTotals = useMemo(
    () => calculateDailyTotals(allExcursions, year, month),
    [allExcursions, year, month]
  );

  const headerContent = (
    <>
      <Typography variant="h6" gutterBottom>
        Report Results:
        {' '}
        {projectTitle}
        {' - '}
        {monthLabel}
      </Typography>
      <StyledDivider />
    </>
  );

  if (normalizedItems.length === 0) {
    return (
      <ResultsSection elevation={1}>
        {headerContent}
        <Box py={4} textAlign="center">
          <Typography variant="body1" color="textSecondary">
            No sites or applications were found for this project.
          </Typography>
        </Box>
      </ResultsSection>
    );
  }

  return (
    <ResultsSection elevation={1}>
      {headerContent}

      <SummaryBox>
        <SummaryItem>
          <Typography variant="h4" color="error">
            {totalExcursions}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total Excursions
          </Typography>
        </SummaryItem>
        <SummaryItem>
          <Typography variant="h4" color="primary">
            {affectedItems}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Affected
            {' '}
            {labels.groupLabel}
            /
            {labels.itemLabel}
          </Typography>
        </SummaryItem>
      </SummaryBox>

      <SectionTitle variant="subtitle1" gutterBottom>
        Daily Excursions Overview
      </SectionTitle>
      <ChartContainer>
        <ExcursionChart
          data={dailyTotals}
          title={`Daily Excursions - ${monthLabel}`}
        />
      </ChartContainer>

      <BreakdownTitle variant="subtitle1" gutterBottom>
        Breakdown by
        {' '}
        {labels.groupLabel}
        {' / '}
        {labels.itemLabel}
      </BreakdownTitle>

      {normalizedItems.map(({ group, item, excursions }) => (
        <ItemSection
          key={`${group.id}-${item.id}`}
          group={group}
          item={item}
          excursions={excursions}
          year={year}
          month={month}
        />
      ))}
    </ResultsSection>
  );
};

export default ReportResults;
